import { ManquesFiche } from '../ManquesFiche.js';
import { Button, Checkbox, Dialog, InventoryNumber, Price } from '@artosera/ui';
import type { ActionLotOeuvres, RapportLotOeuvres } from '@artosera/shared';
import { ArrowRight } from 'lucide-react';
import { useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import { useLieux } from '../../../api/lieux.js';
import { useApercuLot, useAppliquerLot } from '../../../api/lotOeuvres.js';
import { useShellContext } from '../../shell/context.js';
import { FormulaireLot } from './FormulaireLot.js';
import { TableauApercuLot } from './TableauApercuLot.js';
import { PARAMETRES_VIDES, construireAction, type ParametresLot } from './parametresLot.js';
import styles from './LotOeuvresDialog.module.css';

type Etape = 'parametres' | 'apercu' | 'confirmation' | 'rapport';
const ETAPES_VISIBLES = ['parametres', 'apercu', 'confirmation'] as const;

/** Total avant / après des lignes modifiées, par devise (les montants viennent de l'aperçu). */
function totauxPrix(
  rapport: RapportLotOeuvres,
): readonly { devise: string; avant: number; apres: number }[] {
  const parDevise = new Map<string, { avant: number; apres: number }>();
  for (const ligne of rapport.lignes) {
    if (ligne.resultat !== 'modifiee') continue;
    const avant = ligne.avant?.['prixPublicCentimes'];
    const apres = ligne.apres?.['prixPublicCentimes'];
    const devise = ligne.apres?.['devise'];
    if (typeof avant !== 'number' || typeof apres !== 'number' || typeof devise !== 'string') {
      continue;
    }
    const total = parDevise.get(devise) ?? { avant: 0, apres: 0 };
    parDevise.set(devise, { avant: total.avant + avant, apres: total.apres + apres });
  }
  return [...parDevise].map(([devise, total]) => ({ devise, ...total }));
}

function estApercuPerime(erreur: unknown): boolean {
  if (!estApiError(erreur) || erreur.status !== 409) return false;
  const details = erreur.details as { raison?: unknown } | null;
  return details?.raison === 'apercu_perime';
}

/**
 * La modification groupée d'œuvres, en quatre temps : réglage, aperçu (rien
 * n'est écrit), confirmation, rapport. Les lignes, leur ordre et les montants
 * viennent de l'aperçu de l'API ; la régie ne calcule aucun prix.
 */
export function LotOeuvresDialog({
  type,
  oeuvreIds,
  parametresInitiaux,
  onAnnuler,
  onTermine,
}: {
  type: ActionLotOeuvres;
  oeuvreIds: readonly string[];
  /** Réglage connu d'avance (le stand où l'on place des œuvres) ; l'aperçu reste à relire. */
  parametresInitiaux?: Partial<ParametresLot>;
  /** Fermeture avant toute écriture : la sélection reste telle quelle. */
  onAnnuler: () => void;
  /** Fermeture après l'écriture : les refusées à garder sélectionnées, ou `null` pour tout vider. */
  onTermine: (refusees: readonly string[] | null) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { galerieSlug } = useShellContext();
  const apercu = useApercuLot(galerieSlug);
  const appliquer = useAppliquerLot(galerieSlug);
  const lieux = useLieux(galerieSlug, true);
  const tr = (cle: string, valeurs?: Record<string, unknown>): string =>
    t(`regie:oeuvres.lot.${cle}`, valeurs);

  const [etape, setEtape] = useState<Etape>('parametres');
  const [parametres, setParametres] = useState<ParametresLot>({
    ...PARAMETRES_VIDES,
    ...parametresInitiaux,
  });
  const [rapportApercu, setRapportApercu] = useState<RapportLotOeuvres | null>(null);
  const [rapportFinal, setRapportFinal] = useState<RapportLotOeuvres | null>(null);
  const [refuseesSeulement, setRefuseesSeulement] = useState(false);
  const [relu, setRelu] = useState(false);
  const [perime, setPerime] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Une clé par confirmation : un nouvel essai après une coupure renvoie la même.
  const [cleOperation, setCleOperation] = useState<string | null>(null);

  const action = useMemo(() => construireAction(type, parametres), [type, parametres]);
  const lieuParId = useMemo(
    () => new Map((lieux.data ?? []).map((lieu) => [lieu.id, lieu.nom] as const)),
    [lieux.data],
  );
  const messageErreur = (e: unknown): string =>
    estApiError(e) ? e.message : t('commun:erreurReseau');

  async function voirApercu(apresPeremption = false): Promise<void> {
    if (!action) return;
    setErreur(null);
    try {
      const rapport = await apercu.mutateAsync({ oeuvreIds: [...oeuvreIds], action });
      setRapportApercu(rapport);
      setRefuseesSeulement(false);
      setCleOperation(null);
      setRelu(false);
      setPerime(apresPeremption);
      setEtape('apercu');
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }

  function passerALaConfirmation(): void {
    setCleOperation(crypto.randomUUID());
    setRelu(false);
    setErreur(null);
    setEtape('confirmation');
  }

  async function confirmer(): Promise<void> {
    if (!action || !rapportApercu || !cleOperation) return;
    setErreur(null);
    try {
      const rapport = await appliquer.mutateAsync({
        demande: { oeuvreIds: [...oeuvreIds], action },
        empreinteApercu: rapportApercu.lot.empreinte,
        cleOperation,
      });
      setRapportFinal(rapport);
      setEtape('rapport');
    } catch (e) {
      if (estApercuPerime(e)) {
        // Des œuvres ont changé : on relit l'état, on ne force rien.
        await voirApercu(true);
        return;
      }
      setErreur(messageErreur(e));
    }
  }

  const modifiees = rapportApercu?.compteurs.modifiees ?? 0;
  const refuseesFinales = (rapportFinal?.lignes ?? []).filter(
    (ligne) => ligne.resultat === 'refusee',
  );

  function fermer(): void {
    if (etape === 'rapport') onTermine(null);
    else onAnnuler();
  }

  let corps: JSX.Element;
  let pied: JSX.Element;
  if (etape === 'parametres') {
    corps = (
      <FormulaireLot
        type={type}
        galerieSlug={galerieSlug}
        langue={langue}
        parametres={parametres}
        onChange={(suite) => setParametres((avant) => ({ ...avant, ...suite }))}
      />
    );
    pied = (
      <>
        <Button variant="ghost" onClick={onAnnuler}>
          {t('commun:actions.annuler')}
        </Button>
        <Button disabled={!action} loading={apercu.isPending} onClick={() => void voirApercu()}>
          {tr('voirApercu')}
        </Button>
      </>
    );
  } else if (etape === 'apercu' && rapportApercu) {
    const { compteurs, lignes } = rapportApercu;
    const visibles = refuseesSeulement
      ? lignes.filter((ligne) => ligne.resultat === 'refusee')
      : lignes;
    const totaux = type === 'prix' ? totauxPrix(rapportApercu) : [];
    corps = (
      <>
        {perime ? (
          <p className={styles.alerte} role="status">
            {tr('apercu.perime')}
          </p>
        ) : null}
        <ul className={styles.compteurs} aria-label={tr('apercu.compteurs')}>
          {(['modifiees', 'inchangees', 'refusees'] as const).map((cle) => (
            <li key={cle} className={styles.compteur} data-cle={cle}>
              <strong>{compteurs[cle]}</strong>
              <span>{tr(`apercu.${cle}`, { count: compteurs[cle] })}</span>
            </li>
          ))}
        </ul>
        {totaux.length > 0 ? <TotauxPrix totaux={totaux} langue={langue} /> : null}
        {compteurs.refusees > 0 ? (
          <Checkbox
            id="lot-refusees-seulement"
            label={tr('apercu.refuseesSeulement')}
            checked={refuseesSeulement}
            onCheckedChange={(valeur) => setRefuseesSeulement(valeur === true)}
          />
        ) : null}
        <TableauApercuLot lignes={visibles} type={type} langue={langue} lieuParId={lieuParId} />
        {modifiees === 0 ? <p className={styles.aide}>{tr('apercu.rienAFaire')}</p> : null}
      </>
    );
    pied = (
      <>
        <Button variant="ghost" onClick={() => setEtape('parametres')}>
          {tr('modifierReglage')}
        </Button>
        <Button disabled={modifiees === 0} onClick={passerALaConfirmation}>
          {tr('continuer')}
        </Button>
      </>
    );
  } else if (etape === 'confirmation' && rapportApercu) {
    const { compteurs } = rapportApercu;
    const totaux = type === 'prix' ? totauxPrix(rapportApercu) : [];
    corps = (
      <>
        <p className={styles.phrase}>{tr(`confirmation.${type}`, { count: modifiees })}</p>
        {compteurs.refusees + compteurs.inchangees > 0 ? (
          <p className={styles.aide}>
            {tr('confirmation.laissees', {
              detail: [
                compteurs.refusees > 0
                  ? tr('confirmation.laisseesRefusees', { count: compteurs.refusees })
                  : null,
                compteurs.inchangees > 0
                  ? tr('confirmation.laisseesInchangees', { count: compteurs.inchangees })
                  : null,
              ]
                .filter(Boolean)
                .join(', '),
            })}
          </p>
        ) : null}
        {totaux.length > 0 ? <TotauxPrix totaux={totaux} langue={langue} /> : null}
        {type === 'publication' && action?.type === 'publication' && action.visible ? (
          <>
            {rapportApercu.lignes
              .filter((ligne) => ligne.resultat === 'modifiee' && (ligne.manques?.length ?? 0) > 0)
              .map((ligne) => (
                <div key={ligne.oeuvreId}>
                  <strong>{ligne.titre}</strong>
                  <ManquesFiche manques={ligne.manques ?? []} />
                </div>
              ))}
            {rapportApercu.lignes.some(
              (ligne) => ligne.resultat === 'modifiee' && (ligne.manques?.length ?? 0) > 0,
            ) ? (
              <p>{t('regie:oeuvres.completude.confirmerLot')}</p>
            ) : null}
          </>
        ) : null}
        {type === 'prix' ? (
          <Checkbox
            id="lot-prix-relus"
            label={tr('confirmation.prixRelus')}
            checked={relu}
            onCheckedChange={(valeur) => setRelu(valeur === true)}
          />
        ) : null}
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
      </>
    );
    pied = (
      <>
        <Button variant="ghost" onClick={() => setEtape('apercu')} disabled={appliquer.isPending}>
          {tr('retourApercu')}
        </Button>
        <Button
          disabled={modifiees === 0 || (type === 'prix' && !relu)}
          loading={appliquer.isPending || apercu.isPending}
          onClick={() => void confirmer()}
        >
          {tr('appliquer', { count: modifiees })}
        </Button>
      </>
    );
  } else if (rapportFinal) {
    const { compteurs } = rapportFinal;
    corps = (
      <>
        <p className={styles.phrase} role="status">
          {tr('rapport.ecrit', { count: compteurs.modifiees })}
        </p>
        {compteurs.inchangees > 0 ? (
          <p className={styles.aide}>{tr('rapport.inchangees', { count: compteurs.inchangees })}</p>
        ) : null}
        {refuseesFinales.length > 0 ? (
          <section aria-labelledby="lot-refusees-titre">
            <h3 id="lot-refusees-titre" className={styles.sousTitre}>
              {tr('rapport.refusees', { count: refuseesFinales.length })}
            </h3>
            <ul className={styles.refusees}>
              {refuseesFinales.map((ligne) => (
                <li key={ligne.oeuvreId}>
                  <span className={styles.titreLigne}>
                    {ligne.titre ?? tr('ligne.introuvable')}
                  </span>
                  {ligne.numeroInventaire ? (
                    <InventoryNumber value={ligne.numeroInventaire} />
                  ) : null}
                  <span className={styles.motif}>{ligne.motif}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </>
    );
    pied = (
      <>
        {refuseesFinales.length > 0 ? (
          <Button
            variant="secondary"
            onClick={() => onTermine(refuseesFinales.map((ligne) => ligne.oeuvreId))}
          >
            {tr('rapport.garderRefusees')}
          </Button>
        ) : null}
        <Button onClick={() => onTermine(null)}>{t('commun:actions.fermer')}</Button>
      </>
    );
  } else {
    corps = <></>;
    pied = <></>;
  }

  const indexEtape = ETAPES_VISIBLES.findIndex((cle) => cle === etape);

  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert) fermer();
      }}
      title={tr(`titre.${type}`)}
      description={tr('portee', { count: oeuvreIds.length })}
      closeLabel={t('commun:actions.fermer')}
      footer={pied}
    >
      <div className={styles.racine} data-large={etape === 'parametres' ? undefined : 'true'}>
        <ol className={styles.etapes} aria-label={tr('etapes.libelle')}>
          {ETAPES_VISIBLES.map((cle, index) => (
            <li
              key={cle}
              aria-current={index === indexEtape ? 'step' : undefined}
              data-fait={etape === 'rapport' || index < indexEtape ? 'true' : undefined}
            >
              {tr(`etapes.${cle}`)}
            </li>
          ))}
        </ol>
        {etape === 'parametres' && erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {corps}
      </div>
    </Dialog>
  );
}

function TotauxPrix({
  totaux,
  langue,
}: {
  totaux: readonly { devise: string; avant: number; apres: number }[];
  langue: 'fr' | 'en';
}): JSX.Element {
  const { t } = useTranslation('regie');
  return (
    <dl className={styles.totaux}>
      {totaux.map(({ devise, avant, apres }) => (
        <div key={devise}>
          <dt>{t('oeuvres.lot.apercu.totalPrix', { devise })}</dt>
          <dd>
            <Price cents={avant} lang={langue} currency={devise} />
            <ArrowRight size={14} strokeWidth={1.5} aria-label={t('oeuvres.lot.ligne.devient')} />
            <Price cents={apres} lang={langue} currency={devise} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
